import React, { useState, useEffect, useMemo } from "react";
import { Component, ComponentType, FermentationTankCardProperties, Viewport } from "@/types";
import { Thermometer, Droplet, AlertTriangle, Clock, Eye, Settings, ExternalLink } from "lucide-react";
import { FermentationTankIcon } from "@/components/icons/FermentationTankIcon";
import { useInteractionContext } from "@/components/shared/rendering/InteractionContextRuntime";
import { useSubComponentRegistry, SubComponentDefinition } from "@/hooks/useSubComponentRegistry";
import { SubComponentWrapper } from "@/components/shared/SubComponentWrapper";

type TankActionButton = NonNullable<FermentationTankCardProperties["actionButtons"]>[number] & {
  action?: string;
  target?: string;
};

type TankIconRenderer = (props: { size?: number }) => React.ReactNode;

interface FermentationTankCardProps {
  component: Component;
  isPreview?: boolean;
  viewport?: Viewport;  // Optional viewport from editor context for responsive layout
}

// Hook to detect if viewport is mobile/tablet for compact layout
const useCompactLayout = (mobileBreakpoint: number = 768) => {
  const [isCompact, setIsCompact] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < mobileBreakpoint;
    }
    return false;
  });

  useEffect(() => {
    const checkWidth = () => {
      const shouldBeCompact = window.innerWidth < mobileBreakpoint;
      setIsCompact(shouldBeCompact);
    };

    checkWidth();

    window.addEventListener('resize', checkWidth);
    return () => window.removeEventListener('resize', checkWidth);
  }, [mobileBreakpoint]);

  return isCompact;
};

interface TankData {
  fillLevel: number;
  currentTemp: number;
  targetTemp: number;
  temperatureUnit: "F" | "C";
  glycolStatus: "active" | "inactive";
  batchNumber?: string;
  beerType?: string;
  startDate?: string;
  projectedEnd?: string;
  alerts?: Array<{
    id: string;
    severity: "info" | "warning" | "error";
    message: string;
    timestamp?: string;
  }>;
}

// Simple data fetching hook
const useTankData = (
  tankId: string,
  dataEndpoint?: string,
  refreshInterval?: number,
  staticData?: Partial<TankData>
) => {
  const [data, setData] = useState<TankData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    fillLevel: staticFillLevel,
    currentTemp: staticCurrentTemp,
    targetTemp: staticTargetTemp,
    temperatureUnit: staticTemperatureUnit,
    glycolStatus: staticGlycolStatus,
    batchNumber: staticBatchNumber,
    beerType: staticBeerType,
    startDate: staticStartDate,
    projectedEnd: staticProjectedEnd,
    alerts: staticAlerts,
  } = staticData ?? {};

  useEffect(() => {
    // If no endpoint, use static data
    if (!dataEndpoint) {
      setData({
        fillLevel: staticFillLevel ?? 70,
        currentTemp: staticCurrentTemp ?? 140,
        targetTemp: staticTargetTemp ?? 130,
        temperatureUnit: staticTemperatureUnit ?? "F",
        glycolStatus: staticGlycolStatus ?? "active",
        batchNumber: staticBatchNumber,
        beerType: staticBeerType,
        startDate: staticStartDate,
        projectedEnd: staticProjectedEnd,
        alerts: staticAlerts,
      });
      return undefined;
    }

    // Fetch from endpoint
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetch(dataEndpoint);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const json = await response.json();
        setData(json);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch");
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Set up polling if refreshInterval is provided
    if (refreshInterval && refreshInterval > 0) {
      const interval = setInterval(fetchData, refreshInterval);
      return () => clearInterval(interval);
    }

    return undefined;
  }, [
    tankId,
    dataEndpoint,
    refreshInterval,
    staticFillLevel,
    staticCurrentTemp,
    staticTargetTemp,
    staticTemperatureUnit,
    staticGlycolStatus,
    staticBatchNumber,
    staticBeerType,
    staticStartDate,
    staticProjectedEnd,
    staticAlerts,
  ]);

  return { data, loading, error };
};

export const FermentationTankCard: React.FC<FermentationTankCardProps> = ({
  component,
  isPreview: _isPreview = false,
  viewport,
}) => {
  const { properties = {}, style = {} } = component;
  const props = properties as Partial<FermentationTankCardProperties>;

  // Get interaction context for navigation
  const interaction = useInteractionContext();

  // Detect compact layout based on viewport (editor context) or window width (viewer)
  const windowBasedIsCompact = useCompactLayout(768);
  // Use viewport prop if available (editor context), otherwise fall back to window width
  // Treat both MOBILE and TABLET viewports as compact (stacked action buttons)
  const isCompact = viewport
    ? (viewport === Viewport.MOBILE || viewport === Viewport.TABLET)
    : windowBasedIsCompact;

  // Extract all properties with defaults
  const tankId = props.tankId || "Tank 1";
  const label = props.label || tankId;
  const dataEndpoint = props.dataEndpoint;
  const refreshInterval = props.refreshInterval ?? 5000;

  // Static data
  const staticData = {
    fillLevel: props.fillLevel,
    currentTemp: props.currentTemp,
    targetTemp: props.targetTemp,
    temperatureUnit: props.temperatureUnit,
    glycolStatus: props.glycolStatus,
    batchNumber: props.batchNumber,
    beerType: props.beerType,
    startDate: props.startDate,
    projectedEnd: props.projectedEnd,
    alerts: props.alerts,
  };

  // Fetch tank data
  const { data: tankData } = useTankData(
    tankId,
    dataEndpoint,
    refreshInterval,
    staticData
  );

  const currentTempColor = props.currentTempColor ?? "#E27D3D"; // Desert Sun (warning/attention)
  const targetTempColor = props.targetTempColor ?? "#1CA9CC"; // Caribbean Blue (success/target)
  const tempLabelColor = props.tempLabelColor ?? "#6E6E6E"; // Mid Gray

  const alertColorInfo = props.alertColorInfo ?? "#116B98"; // Blue Grotto
  const alertColorWarning = props.alertColorWarning ?? "#E27D3D"; // Desert Sun
  const alertColorError = props.alertColorError ?? "#D9534F"; // Error red from style guide

  const cardBackgroundColor = props.cardBackgroundColor ?? "#F4F5F4"; // Light Gray
  const cardBorderColor = props.cardBorderColor ?? "#DADADA"; // Neutral border from style guide
  const cardBorderRadius = props.cardBorderRadius ?? 4; // Standard 4px from style guide
  const cardPadding = props.cardPadding ?? "24px"; // 24px padding per style guide

  // Layout control
  const tankVisualWidth = props.tankVisualWidth ?? "40%";
  const statusDisplayWidth = props.statusDisplayWidth ?? "60%";

  const showGlycolStatus = props.showGlycolStatus !== false;
  const showBatchInfo = props.showBatchInfo !== false;
  const showAlerts = props.showAlerts !== false;
  const showActionButtons = props.showActionButtons !== false;

  // View Details button properties
  const showViewDetailsButton = props.showViewDetailsButton !== false;
  const viewDetailsLabel = props.viewDetailsLabel || "View Details";
  const viewDetailsAction = props.viewDetailsAction;
  const viewDetailsTarget = props.viewDetailsTarget;

  const sectionGap = props.sectionGap ?? 12;
  const cardGap = props.cardGap ?? 16;

  // Default action buttons - only Override by default
  const actionButtons = useMemo<TankActionButton[]>(() => props.actionButtons ?? [
    { id: "override", label: "Override", icon: "Settings", variant: "outline" as const, visible: true },
  ], [props.actionButtons]);

  // Get data values
  const currentTemp = tankData?.currentTemp ?? 140;
  const targetTemp = tankData?.targetTemp ?? 130;
  const temperatureUnit = tankData?.temperatureUnit ?? "F";
  const glycolStatus = tankData?.glycolStatus ?? "active";
  const batchNumber = tankData?.batchNumber ?? "ABW-2024-047";
  const beerType = tankData?.beerType ?? "West Coast IPA";
  const startDate = tankData?.startDate ?? "Nov 28, 2024";
  const projectedEnd = tankData?.projectedEnd ?? "Dec 12, 2024";
  const alerts = tankData?.alerts ?? [
    { id: "1", severity: "error" as const, message: "+2°F above setpoint" }
  ];

  // Define sub-components for selectability in editor
  const subComponentDefs: SubComponentDefinition[] = useMemo(() => [
    {
      key: 'view-details',
      type: ComponentType.BUTTON,
      subComponentType: 'button',
      name: 'View Details Button',
      properties: {
        label: viewDetailsLabel,
        action: viewDetailsAction || 'navigate',
        target: viewDetailsTarget,
      },
    },
    {
      key: 'tank-label',
      type: ComponentType.TEXT,
      subComponentType: 'label',
      name: 'Tank Label',
      properties: {
        content: label,
      },
    },
    {
      key: 'tank-icon',
      type: ComponentType.ICON,
      subComponentType: 'icon',
      name: 'Tank Icon',
      properties: {
        iconName: 'FermentationTank',
        library: 'custom',
        size: 160,
        color: '#1CA9CC',
      },
    },
    {
      key: 'current-temp',
      type: ComponentType.TEXT,
      subComponentType: 'label',
      name: 'Current Temperature',
      properties: {
        content: `${currentTemp}°${temperatureUnit}`,
        color: currentTempColor,
      },
    },
    {
      key: 'target-temp',
      type: ComponentType.TEXT,
      subComponentType: 'label',
      name: 'Target Temperature',
      properties: {
        content: `${targetTemp}°${temperatureUnit}`,
        color: targetTempColor,
      },
    },
    {
      key: 'glycol-status',
      type: ComponentType.TEXT,
      subComponentType: 'label',
      name: 'Glycol Status',
      properties: {
        content: glycolStatus === "active" ? "Active" : "Inactive",
        color: glycolStatus === "active" ? "#1CA9CC" : "#6E6E6E",
      },
    },
    // Add action buttons as sub-components
    ...actionButtons.filter(btn => btn.visible !== false).map(btn => ({
      key: `action-${btn.id}`,
      type: ComponentType.BUTTON,
      subComponentType: 'button',
      name: btn.label,
      properties: {
        label: btn.label,
        icon: btn.icon,
        variant: btn.variant,
        action: btn.action,
        target: btn.target,
      },
    })),
  ], [
    viewDetailsLabel, viewDetailsAction, viewDetailsTarget, label,
    currentTemp, temperatureUnit, currentTempColor,
    targetTemp, targetTempColor, glycolStatus, actionButtons
  ]);

  // Register sub-components for editor selectability
  const { getSubComponentId, getSubComponent } = useSubComponentRegistry(
    component,
    subComponentDefs
  );

  // Get updated properties from sub-components (may be modified via AI chat)
  const viewDetailsSub = getSubComponent('view-details');
  const effectiveViewDetailsTarget = viewDetailsSub?.properties?.target as string || viewDetailsTarget;
  const effectiveViewDetailsAction = viewDetailsSub?.properties?.action as string || viewDetailsAction;

  // Icon mapping
  const iconMap: Record<string, TankIconRenderer> = {
    Clock: (iconProps) => <Clock {...iconProps} />,
    Eye: (iconProps) => <Eye {...iconProps} />,
    Settings: (iconProps) => <Settings {...iconProps} />,
    FermentationTank: (iconProps) => <FermentationTankIcon {...iconProps} />,
    Tank: (iconProps) => <FermentationTankIcon {...iconProps} />,
  };

  // View Details click handler - uses effective values from sub-component (may be AI-modified)
  const handleViewDetailsClick = async () => {
    if (effectiveViewDetailsAction === "navigate" && effectiveViewDetailsTarget) {
      // Use internal navigation system
      if (interaction?.handlers?.onNavigate) {
        await interaction.handlers.onNavigate({
          targetPageId: effectiveViewDetailsTarget,
        });
      }
    } else if (effectiveViewDetailsTarget) {
      // External URL - open in new tab
      window.open(effectiveViewDetailsTarget, "_blank", "noopener,noreferrer");
    }
  };

  // Card container styles
  // Strip width-related properties from component.style - sizing is handled by ComponentWrapper/FlexContainer
  const { width: _w, minWidth: _mw, maxWidth: _xw, ...visualStyle } = (style || {}) as React.CSSProperties;
  const cardStyle: React.CSSProperties = {
    ...visualStyle,
    display: "flex",
    flexDirection: "column",
    gap: `${cardGap}px`,
    padding: cardPadding,
    backgroundColor: cardBackgroundColor,
    borderRadius: `${cardBorderRadius}px`,
    border: `1px solid ${cardBorderColor}`,
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    width: "100%",
    height: "auto",
  };

  // Content row styles - stack vertically in compact mode
  const contentRowStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: isCompact ? "column" : "row",
    gap: isCompact ? "24px" : "24px",
    width: "100%",
    alignItems: isCompact ? "center" : "flex-start",
  };

  // Tank visual section styles - full width and centered in compact mode
  const tankVisualStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: isCompact ? "8px" : "12px",
    width: isCompact ? "100%" : tankVisualWidth,
    flexGrow: 0,
    flexShrink: isCompact ? 0 : 1,
    flexBasis: isCompact ? "auto" : tankVisualWidth,
  };

  // Status display section styles - full width in compact mode
  const statusDisplayStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: `${sectionGap}px`,
    padding: isCompact ? "0" : "16px",
    width: isCompact ? "100%" : statusDisplayWidth,
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: isCompact ? "auto" : statusDisplayWidth,
  };

  // Alert color based on severity
  const getAlertColor = (severity: "info" | "warning" | "error") => {
    switch (severity) {
      case "info": return alertColorInfo;
      case "warning": return alertColorWarning;
      case "error": return alertColorError;
      default: return alertColorError;
    }
  };

  // Alert background colors - lighter tints of status colors
  const getAlertBgColor = (severity: "info" | "warning" | "error") => {
    switch (severity) {
      case "info": return "#E8F4FA"; // Light Blue Grotto tint
      case "warning": return "#FDF3EB"; // Light Desert Sun tint
      case "error": return "#FCEEED"; // Light error red tint
      default: return "#FCEEED";
    }
  };

  // Alert text colors - darker versions for readability
  const getAlertTextColor = (severity: "info" | "warning" | "error") => {
    switch (severity) {
      case "info": return "#0D5276"; // Darker Blue Grotto
      case "warning": return "#B5602A"; // Darker Desert Sun
      case "error": return "#A3423F"; // Darker error red
      default: return "#A3423F";
    }
  };

  return (
    <div style={cardStyle}>
      {/* Header Row with View Details - wrapped for selectability */}
      {showViewDetailsButton && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            width: "100%",
            marginBottom: "-8px",
          }}
        >
          <SubComponentWrapper subComponentId={getSubComponentId('view-details')} inline>
            <button
              onClick={handleViewDetailsClick}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 12px",
                backgroundColor: "transparent",
                border: "none",
                borderRadius: "4px", // Standard 4px from style guide
                color: "#6E6E6E", // Mid Gray from style guide
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
                transition: "color 0.2s ease-in-out", // Per style guide timing
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "#1CA9CC"; // Forest Green on hover
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "#6E6E6E";
              }}
            >
              {viewDetailsLabel}
              <ExternalLink size={16} />
            </button>
          </SubComponentWrapper>
        </div>
      )}

      {/* Tank Content Row */}
      <div style={contentRowStyle}>
        {/* Tank Visual Section (40% on desktop, full width row on mobile) */}
        <div style={tankVisualStyle}>
          {/* Tank Label - wrapped for selectability */}
          <SubComponentWrapper subComponentId={getSubComponentId('tank-label')}>
            <div
              style={{
                fontSize: isCompact ? "18px" : "20px",
                fontWeight: 600, // SemiBold per style guide
                textAlign: "center",
                color: "#0A0A0A", // Charcoal from style guide
                lineHeight: 1.2,
                margin: isCompact ? "0" : "0 0 12px 0",
              }}
            >
              {label}
            </div>
          </SubComponentWrapper>

          {/* Fermentation Tank Icon with temperature overlay */}
          <SubComponentWrapper subComponentId={getSubComponentId('tank-icon')}>
            <div style={{ position: "relative", display: "inline-flex" }}>
              <FermentationTankIcon
                size={isCompact ? 200 : 280}
                color="#D1D5DB"
                strokeWidth={1}
              />
              {/* Temperature display centered on tank */}
              <div
                style={{
                  position: "absolute",
                  top: "38%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "2px",
                }}
              >
                <span
                  style={{
                    fontSize: isCompact ? "32px" : "42px",
                    fontWeight: 700,
                    color: currentTempColor,
                    lineHeight: 1,
                  }}
                >
                  {currentTemp}°{temperatureUnit}
                </span>
              </div>
            </div>
          </SubComponentWrapper>

          {/* Alerts Section - under the gauge */}
          {showAlerts && alerts && alerts.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", width: "100%", marginTop: "8px" }}>
              {/* Alerts Header */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: "8px" }}>
                <span style={{ fontSize: "14px", fontWeight: 600, color: tempLabelColor }}>
                  Alerts
                </span>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: 600,
                    textAlign: "center",
                    color: "#ffffff",
                    backgroundColor: alertColorError,
                    borderRadius: "999px",
                    padding: "3px 7px",
                    lineHeight: 1,
                  }}
                >
                  {alerts.length}
                </span>
              </div>

              {/* Alert Items */}
              {alerts.map((alert) => (
                <div
                  key={alert.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px 10px",
                    backgroundColor: getAlertBgColor(alert.severity),
                    borderRadius: "4px",
                  }}
                >
                  <AlertTriangle size={16} color={getAlertColor(alert.severity)} strokeWidth={2} />
                  <span
                    style={{
                      fontSize: "13px",
                      fontWeight: 500,
                      color: getAlertTextColor(alert.severity),
                      lineHeight: 1.4,
                    }}
                  >
                    {alert.message}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Status Display Section (60%) */}
        <div style={statusDisplayStyle}>
          {/* Status Header */}
          <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "8px" }}>
            <Thermometer size={18} color="#1CA9CC" strokeWidth={2} /> {/* Forest Green */}
            <span
              style={{
                fontSize: "14px",
                fontWeight: 600,
                color: "#0A0A0A", // Charcoal from style guide
                lineHeight: 1.2,
              }}
            >
              Status
            </span>
          </div>

          {/* Glycol Status Row - wrapped for selectability */}
          {showGlycolStatus && (
            <SubComponentWrapper subComponentId={getSubComponentId('glycol-status')}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "8px",
                  padding: "4px 0",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Droplet
                    size={16}
                    color="#116B98" // Blue Grotto for water/glycol
                    strokeWidth={2}
                  />
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: tempLabelColor,
                      lineHeight: 1.4,
                    }}
                  >
                    Glycol:
                  </span>
                </div>
                <span
                  style={{
                    fontSize: "18px",
                    fontWeight: 600,
                    color: glycolStatus === "active" ? "#1CA9CC" : "#6E6E6E", // Forest Green or Mid Gray
                    lineHeight: 1,
                  }}
                >
                  {glycolStatus === "active" ? "Active" : "Inactive"}
                </span>
              </div>
            </SubComponentWrapper>
          )}

          {/* Current Temperature Row - wrapped for selectability */}
          <SubComponentWrapper subComponentId={getSubComponentId('current-temp')}>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "baseline",
                gap: "8px",
                padding: "4px 0",
              }}
            >
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: tempLabelColor,
                  lineHeight: 1.4,
                }}
              >
                Current:
              </span>
              <span
                style={{
                  fontSize: "24px",
                  fontWeight: 700,
                  textAlign: "right",
                  color: currentTempColor,
                  lineHeight: 1,
                }}
              >
                {currentTemp}°{temperatureUnit}
              </span>
            </div>
          </SubComponentWrapper>

          {/* Target Temperature Row - wrapped for selectability */}
          <SubComponentWrapper subComponentId={getSubComponentId('target-temp')}>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "baseline",
                gap: "8px",
                padding: "4px 0",
              }}
            >
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: tempLabelColor,
                  lineHeight: 1.4,
                }}
              >
                Target:
              </span>
              <span
                style={{
                  fontSize: "18px",
                  fontWeight: 600,
                  textAlign: "right",
                  color: targetTempColor,
                  lineHeight: 1,
                }}
              >
                {targetTemp}°{temperatureUnit}
              </span>
            </div>
          </SubComponentWrapper>

          {/* Divider 1 */}
          {showBatchInfo && (
            <div
              style={{
                borderTop: `1px solid ${cardBorderColor}`,
                marginTop: "8px",
                marginBottom: "8px",
              }}
            />
          )}

          {/* Batch Info Section */}
          {showBatchInfo && (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {/* Batch Number */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "4px 0",
                }}
              >
                <span style={{ fontSize: "13px", fontWeight: 500, color: tempLabelColor }}>
                  Batch #:
                </span>
                <span style={{ fontSize: "13px", fontWeight: 600, color: "#0A0A0A" }}>
                  {batchNumber}
                </span>
              </div>

              {/* Beer Type */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "4px 0",
                }}
              >
                <span style={{ fontSize: "13px", fontWeight: 500, color: tempLabelColor }}>
                  Beer Type:
                </span>
                <span style={{ fontSize: "13px", fontWeight: 600, color: "#0A0A0A" }}>
                  {beerType}
                </span>
              </div>

              {/* Start Date */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "4px 0",
                }}
              >
                <span style={{ fontSize: "13px", fontWeight: 500, color: tempLabelColor }}>
                  Start Date:
                </span>
                <span style={{ fontSize: "13px", fontWeight: 600, color: "#0A0A0A" }}>
                  {startDate}
                </span>
              </div>

              {/* Projected End */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "4px 0",
                }}
              >
                <span style={{ fontSize: "13px", fontWeight: 500, color: tempLabelColor }}>
                  Proj End:
                </span>
                <span style={{ fontSize: "13px", fontWeight: 600, color: targetTempColor }}>
                  {projectedEnd}
                </span>
              </div>
            </div>
          )}

          {/* Action Buttons - Responsive: column on mobile, row on desktop */}
          {showActionButtons && (
            <div
              style={{
                display: "flex",
                flexDirection: isCompact ? "column" : "row",
                justifyContent: "flex-start",
                alignItems: isCompact ? "stretch" : "center",
                gap: isCompact ? "10px" : "8px",
                padding: "8px 0 0 0",
                width: "100%",
              }}
            >
              {actionButtons
                .filter((btn) => btn.visible !== false)
                .map((btn) => {
                  const Icon = btn.icon ? iconMap[btn.icon] : null;
                  const buttonStyle: React.CSSProperties = {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    fontSize: isCompact ? "14px" : "12px",
                    fontWeight: 500,
                    padding: isCompact ? "12px 16px" : "8px 16px",
                    width: isCompact ? "100%" : "auto",
                    flex: isCompact ? "none" : 1,
                    backgroundColor: "transparent",
                    color: "#6E6E6E",
                    border: "1px solid #d1d5db",
                    borderRadius: isCompact ? "6px" : "4px",
                    cursor: "pointer",
                    transition: "all 0.2s ease-in-out",
                  };

                  return (
                    <SubComponentWrapper
                      key={btn.id}
                      subComponentId={getSubComponentId(`action-${btn.id}`)}
                      inline
                      style={{ width: isCompact ? "100%" : "auto", flex: isCompact ? "none" : 1 }}
                    >
                      <button
                        style={buttonStyle}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#F4F5F4";
                          e.currentTarget.style.color = "#0A0A0A";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                          e.currentTarget.style.color = "#6E6E6E";
                        }}
                      >
                        {/* Hide icons on mobile for cleaner look */}
                        {!isCompact && Icon && <Icon size={14} />}
                        <span>{btn.label}</span>
                      </button>
                    </SubComponentWrapper>
                  );
                })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
